<template>
    <div
        class="addContextMenu"
        :style="{ left: addMenuPos.x - 70 + 'px', top: addMenuPos.y - 70 + 'px', 'max-height': addMenuPos.height }"
        @keydown.down="selectDown"
        @keydown.up="selectUp"
        @keydown.enter="handleEnter"
    >
        <input
            id="context-search"
            type="search"
            :placeholder="$t('chatbotv2.search')"
            v-model="query"
            autocomplete="off"
        />
        <button
            class="chatbot-btn"
            v-for="(blockType, index) in filteredBlockTypes"
            @click="addBlock(blockType)"
            :key="blockType"
            :class="{ selected: index === selectedIndex }"
            :id="'block_' + blockType"
        >
            <div>
                <BlockIconPreview style="font-size: 16px" :block-type="blockType" class="img" />
            </div>
            <div class="d-flex align-items-center align-content-center justify-content-start">
                <span class="blockTypeText">{{ $t(`chatbotv2.${blockType}`) }}</span>
            </div>
        </button>
        
        <!-- Custom Blocks -->
        <button
            class="chatbot-btn"
            v-for="(customBlock, index) in filteredCustomBlocks"
            @click="addCustomBlock(customBlock)"
            :key="'custom_' + customBlock.blockId"
            :class="{ selected: (filteredBlockTypes.length + index) === selectedIndex }"
            :id="'custom_block_' + customBlock.blockId"
        >
            <div>
                <span class="material-icons img" style="font-size: 16px; color: #9c27b0; background-color: #f3e5f5 !important;">
                    {{ $_.get(customBlock, 'block.icon', 'extension') }}
                </span>
            </div>
            <div class="d-flex align-items-center align-content-center justify-content-start">
                <span class="blockTypeText">{{ customBlock.customName || $_.get(customBlock, 'block.name', 'Bilinmeyen Blok') }}</span>
            </div>
        </button>
    </div>
</template>

<script>
import _ from "lodash";
import { mapActions, mapState } from "vuex";
import log2 from "../../../../../utils/log2";
import { ActionPalette, BlockStyles } from "../../chatbot";
import BlockIconPreview from "./common/BlockIconPreview";
import BLOCK_TYPES from "../../block.types";

const log = log2("AddContextMenu");
export default {
    name: "AddContextMenu",
    components: { BlockIconPreview },
    props: [
        "addMenuPos",
        "block",
        "navigation",
        "storyId",
        "storyVersion",
        "showNotification",
        "addBlockToDiagram",
        "flattedDiagram",
        "blocksMetas",
        "getAllowedBlocks",
    ],
    data() {
        let otherActions = [
            { name: "Success", id: "SUCCESS" },
            { name: "Failure", id: "FAILURE" },
        ];

        return {
            ActionPalette: _.uniqBy(_.flatMap(ActionPalette, "items"), "name").filter(
                (x) => x.id !== "DEFAULT_FALLBACK"
            ),
            query: "",
            filteredBlockTypes: this.getAllowedBlocks(this.block, BLOCK_TYPES.ALL_BLOCKS),
            filteredCustomBlocks: [],
            BlockStyles,
            blockInfo: null,
            BLOCK_TYPES,
            selectedIndex: 0,
        };
    },
    computed: {
        ...mapState({
            importedCustomBlocks: (state) => state.customBlocks.importedBlocks || [],
        }),
        totalItemsCount() {
            return this.filteredBlockTypes.length + this.filteredCustomBlocks.length;
        },
    },
    methods: {
        ...mapActions({
            addBlockD: "CB/addBlock",
        }),
        selectUp() {
            this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        },
        selectDown() {
            this.selectedIndex = Math.min(this.selectedIndex + 1, this.totalItemsCount - 1);
        },
        handleEnter() {
            if (this.selectedIndex < this.filteredBlockTypes.length) {
                const blockType = this.filteredBlockTypes[this.selectedIndex];
                if (blockType) {
                    this.addBlock(blockType);
                }
            } else {
                const customBlockIndex = this.selectedIndex - this.filteredBlockTypes.length;
                const customBlock = this.filteredCustomBlocks[customBlockIndex];
                if (customBlock) {
                    this.addCustomBlock(customBlock);
                }
            }
        },
        addBlock(blockType) {
            log("addBlock", blockType);
            this.$emit("closeContextMenu");
            this.addBlockToDiagram(this.block.id, blockType);
        },
        addCustomBlock(customBlock) {
            log("addCustomBlock - customBlock:", customBlock);
            this.$emit("closeContextMenu");
            // Use the same format as ActionPalette drag & drop
            const customBlockData = {
                type: "CUSTOM_BLOCK",
                id: customBlock.blockId,
                customBlockId: customBlock.blockId,
                versionId: customBlock.versionId,
                name: customBlock.customName || customBlock.block?.name,
            };
            log("addCustomBlock - customBlockData:", customBlockData);
            log("addCustomBlock - calling addBlockToDiagram with:", this.block.id, "CUSTOM_BLOCK", customBlockData);
            this.addBlockToDiagram(this.block.id, "CUSTOM_BLOCK", customBlockData);
        },
        filterCustomBlocks() {
            if (!this.query) {
                return this.importedCustomBlocks;
            }
            const searchTextLower = this.query.toLowerCase();
            return this.importedCustomBlocks.filter((customBlock) => {
                const name = customBlock.customName || customBlock.block?.name || "";
                return name.toLowerCase().includes(searchTextLower);
            });
        },
        getFilteredBlockTypes() {
            const filteredBlockTypes = _.isEmpty(this.query)
                ? BLOCK_TYPES.ALL_BLOCKS
                : BLOCK_TYPES.ALL_BLOCKS.filter((blockType) =>
                      new RegExp(this.query, "gi").test(this.$t(`chatbotv2.${blockType}`))
                  );
            return filteredBlockTypes;
        },
    },
    mounted() {
        // Load imported custom blocks
        this.$store.dispatch("customBlocks/listImportedBlocks").catch((error) => {
            console.error("Failed to load imported blocks:", error);
        });
        
        setTimeout(() => {
            if (!!document.getElementById("context-search")) return document.getElementById("context-search").focus();
        }, 300);
    },
    watch: {
        importedCustomBlocks: {
            immediate: true,
            handler() {
                this.filteredCustomBlocks = this.filterCustomBlocks();
            },
        },
        query(newQuery) {
            const blocks = _.isEmpty(newQuery)
                ? BLOCK_TYPES.ALL_BLOCKS
                : BLOCK_TYPES.ALL_BLOCKS.filter((blockType) =>
                      new RegExp(this.query, "gi").test(this.$t(`chatbotv2.${blockType}`))
                  );
            this.filteredBlockTypes = this.getAllowedBlocks(this.block, blocks);
            this.filteredCustomBlocks = this.filterCustomBlocks();
        },
        filteredBlockTypes: {
            handler(newFilteredBlockTypes) {
                this.selectedIndex = 0;
            },
            deep: true,
        },
        selectedIndex(newIndex) {
            let el = null;
            if (newIndex < this.filteredBlockTypes.length) {
                const blockType = this.filteredBlockTypes[newIndex];
                if (blockType) {
                    el = document.getElementById(`block_${blockType}`);
                }
            } else {
                const customBlockIndex = newIndex - this.filteredBlockTypes.length;
                const customBlock = this.filteredCustomBlocks[customBlockIndex];
                if (customBlock) {
                    el = document.getElementById(`custom_block_${customBlock.blockId}`);
                }
            }
            
            if (el) {
                el.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
                const isVisible = el.offsetTop > 0;
                log("isVisible", isVisible);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.addContextMenu {
    color: #000000;
    background-color: #ffffff;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    height: auto;
    top: 30px;
    position: absolute;
    max-width: 350px;
    z-index: 9999;
    border-radius: 6px;
    box-shadow: 0 4px 20px #00000040;

    input {
        border: none;
        border-bottom: 1px solid #e3e3e3;
        padding: 0.6rem 1rem;
        position: sticky;
        top: 0;
        z-index: 99999;
    }

    .chatbot-btn {
        border: none;
        background: #fff;
        border-radius: 4px;
        padding: 4px 8px;
        margin: 0 1rem;
        min-height: 34px;
        min-width: 34px;
        color: #000;
        display: flex;
        align-items: center;

        span {
            display: flex;
            gap: 10px;
            align-items: center;
        }

        &:hover,
        &.selected {
            background: #6ca7ff;
            color: #ffffff;
        }

        .img {
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background-color: #06a9f3;
            margin-right: 1rem;
            display: flex;
            justify-content: center;
            align-items: center;
            text-align: center;

            &[data-type="START_POINT"] {
                color: #fff;
                background-color: #566e8a !important;
            }

            &[data-type="DEFAULT_FALLBACK"] {
                color: #fff;
                background-color: #797b8c !important;
            }

            &[data-type="DEFAULT_TIMEOUT"] {
                color: #fff;
                background-color: #d2ba69 !important;
            }

            &[data-type="AI_FUNCTIONS"] {
                color: #fff;
                background-color: #d2ba69 !important;
            }

            &[data-type="TIMEOUT"] {
                color: #fff;
                background-color: #d2ba69 !important;
            }

            &[data-type="BOT_RESPONSE"] {
                color: #647283;
                background-color: #f5f5f5 !important;
            }

            &[data-type="FALLBACK"] {
                color: #ffffff;
                background-color: #797b8c !important;
            }

            &[data-type="FILTER"] {
                color: #fff;
                background-color: #686ad3 !important;
            }

            &[data-type="FLOW"] {
                color: #000;
                background-color: #b3d6ff !important;
            }

            &[data-type="ATTACHMENT_INPUT"] {
                color: #647283;
                background-color: #e7e4e4 !important;
            }

            &[data-type="USER_INPUT"] {
                color: #59687b;
                background-color: #c9d3de !important;
            }

            &[data-type="GO_TO_STEP"] {
                color: #000;
                background-color: #ffdc67 !important;
            }

            &[data-type="ADD_TO_SEGMENT"] {
                color: #22314a;
                background-color: #9ccb80 !important;
            }

            &[data-type="REMOVE_FROM_SEGMENT"] {
                color: #22314a;
                background-color: #f5f5f5 !important;
            }

            &[data-type="WEBHOOK"] {
                color: #fff;
                background-color: #40455b !important;
            }
            &[data-type="API_REQUEST"] {
                color: #fff;
                background-color: #40455b !important;
            }

            &[data-type="SET_ATTRIBUTE"] {
                color: #fff;
                background-color: #6e5b8c !important;
            }

            &[data-type="ASSIGN_OWNER"] {
                color: #fff;
                background-color: #6e5b8c !important;
            }

            &[data-type="QUESTION"] {
                color: #000;
                background-color: #ffad5c !important;
            }

            &[data-type="CLOSE_CHAT"] {
                color: #000;
                background-color: #f5f5f5 !important;
            }

            &[data-type="CLOSE_CHAT"] {
                color: #ffffff;
                background-color: #ad6fe9 !important;
            }

            &[data-type="TRANSFER_CHAT"] {
                color: #000;
                background-color: #76e394 !important;
            }

            &[data-type="TAG_CHAT"] {
                color: #000;
                background-color: #eded67 !important;
            }

            &[data-type="REMOVE_TAG"] {
                color: #000;
                background-color: #eded67 !important;
            }

            &[data-type="SEND_TRANSCRIPT"] {
                color: #ffffff;
                background-color: #4959da !important;
            }
            &[data-type="SEND_MAIL"] {
                color: #ffffff;
                background-color: #ff7a1f !important;
            }

            &[data-type="CREATE_EVENT"] {
                color: #ffffff;
                background-color: #033c7c !important;
            }

            &[data-type="CREATE_RESERVATION"] {
                color: #ffffff;
                background-color: #235603 !important;
            }

            &[data-type="RESERVATION_OPEN_URL"] {
                color: #ffffff;
                background-color: #235603 !important;
            }

            &[data-type="RESERVATION_SELECT_DAY"] {
                color: #ffffff;
                background-color: #235603 !important;
            }

            &[data-type="RESERVATION_EVENT_LIST"] {
                color: #ffffff;
                background-color: #235603 !important;
            }

            &[data-type="RESERVATION_SELECT_TIME"] {
                color: #ffffff;
                background-color: #235603 !important;
            }

            &[data-type="SUPPRESSION_ADD"] {
                color: #ffffff;
                background-color: #9d2126 !important;
            }

            &[data-type="ZOHO_CREATE_RECORD"],
            &[data-type="HOLIDAY_PLUS_GET_HOTELS"],
            &[data-type="HOLIDAY_PLUS_GET_ROOMS"] {
                color: #ffffff;
                background-color: #82d1b2 !important;
            }

            &[data-type="INSTAGRAM_COMMENT_REPLY"],
            &[data-type="INSTAGRAM_COMMENT_DELETE"],
            &[data-type="INSTAGRAM_COMMENT_SHOW"],
            &[data-type="INSTAGRAM_COMMENT_HIDE"],
            &[data-type="INSTAGRAM_COMMENT_REPLY_AS_PRIVATE"] {
                color: #fff;
                background: #833ab4 !important;
                background: linear-gradient(to right, #833ab4, #fd1d1d, #fcb045) !important;
            }

            &[data-type="SUPPRESSION_REMOVE"] {
                color: #ffffff;
                background-color: #9d2126 !important;
            }

            &[data-type="SEND_SMS"] {
                color: #ffffff;
                background-color: #4959da !important;
            }

            &[data-type="SUCCESS"] {
                color: #00c800;
                background-color: #fff !important;
            }

            &[data-type="FAILURE"] {
                color: #ff0000;
                background-color: #fff !important;
            }
        }
    }

    &::-webkit-scrollbar {
        display: none;
    }
}
</style>
